import { http } from '@/api/http';
import { ApiResponseBody, PaginatedApiResponseBody } from '@/api/types';
import {
  CreateOpoContactRequestBody,
  CreateOpoOrganizationRequestBody,
  OpoContact,
  OpoOrganization,
  PaginatedOpoContactListQueryParams,
  PaginatedOpoOrganizationListQueryParams,
  PatchOwnOpoContactRequestBody,
  UpdateOpoContactRequestBody,
  PatchOpoOrganizationRequestBody,
  CreateOpoUserRequestBody,
  UpdateOpoUserRequestBody,
  OpoUserInvitationOperationResponse,
  OpoUserInvitationFlowType,
} from './opo.types';
import { getOpoApiUrl } from './opo.paths';

const getOrganizationList = async (params: PaginatedOpoOrganizationListQueryParams) => {
  const res = await http.get<PaginatedApiResponseBody<OpoOrganization>>(getOpoApiUrl.organizations(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getOrganizationById = async (organizationId: string) => {
  const res = await http.get<ApiResponseBody<OpoOrganization>>(getOpoApiUrl.organizationById(organizationId));
  return res.data.data;
};

const createOrganization = async (body: CreateOpoOrganizationRequestBody) => {
  const res = await http.post<ApiResponseBody<void>>(getOpoApiUrl.organizations(), body);
  return res.data;
};

const updateOrganization = async ({ id, ...body }: PatchOpoOrganizationRequestBody & { id: string }) => {
  const res = await http.patch<ApiResponseBody<void>>(getOpoApiUrl.organizationById(id), body);
  return res.data;
};

const deleteOrganization = async (organizationId: string) => {
  const res = await http.delete<ApiResponseBody<void>>(getOpoApiUrl.organizationById(organizationId));
  return res.data;
};

const getContactList = async (params: PaginatedOpoContactListQueryParams) => {
  const res = await http.get<PaginatedApiResponseBody<OpoContact>>(getOpoApiUrl.contacts(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getContactById = async (contactId: string): Promise<OpoContact> => {
  const res = await http.get<ApiResponseBody<OpoContact>>(getOpoApiUrl.contactById(contactId));
  return res.data.data;
};

const getContactByUserId = async (userId: string): Promise<OpoContact> => {
  const res = await http.get<ApiResponseBody<OpoContact>>(getOpoApiUrl.contactByUserId(userId));
  return res.data.data;
};

const createContact = async (body: CreateOpoContactRequestBody): Promise<void> => {
  await http.post(getOpoApiUrl.contacts(), body);
};

const createUser = async (body: CreateOpoUserRequestBody) => {
  const res = await http.post<OpoUserInvitationOperationResponse>(getOpoApiUrl.opoUsers(), body);
  return res.data;
};

const updateContact = async ({ id, ...body }: UpdateOpoContactRequestBody & { id: string }) => {
  const res = await http.patch<ApiResponseBody<void>>(getOpoApiUrl.contactById(id), body);
  return res.data.data;
};

const updateUser = async ({ id, ...body }: UpdateOpoUserRequestBody & { id: string }) => {
  const res = await http.patch<OpoUserInvitationOperationResponse>(getOpoApiUrl.opoUserByContactId(id), body);
  return res.data;
};

const patchOwnContact = async (body: PatchOwnOpoContactRequestBody) => {
  const res = await http.patch<ApiResponseBody<OpoContact>>(getOpoApiUrl.contacts(), body);
  return res.data.data;
};

const deleteContact = async (contactId: string) => {
  const res = await http.delete<ApiResponseBody<void>>(getOpoApiUrl.contactById(contactId));
  return res.data;
};

const getOperationStatus = async (correlationId: string, flowType: OpoUserInvitationFlowType) => {
  const res = await http.get<ApiResponseBody<OpoUserInvitationOperationResponse>>(
    getOpoApiUrl.opoUserOperationByCorrelationId(correlationId),
    { params: { flowType } },
  );
  return res.data.data;
};

export const opoApiFetcher = {
  getOrganizationList,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getContactList,
  getContactById,
  getContactByUserId,
  createContact,
  createUser,
  updateContact,
  updateUser,
  patchOwnContact,
  deleteContact,
  getOperationStatus,
};
